import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SiPhp,
  SiLaravel,
  SiJavascript,
  SiTypescript,
  SiPython,
  SiTensorflow,
  SiNodedotjs,
  SiExpress,
  SiReact,
  SiVuedotjs,
  SiNextdotjs,
  SiRedux,
  SiMysql,
  SiMongodb,
  SiRedis,
  SiTailwindcss,
  SiBootstrap,
  SiDocker,
  SiGit,
  SiJest,
  SiDotnet,
} from 'react-icons/si';
import { RiJavaLine } from "react-icons/ri";
import { PiFileCSharpBold } from "react-icons/pi";
import { FaCloud } from 'react-icons/fa';

const Skills = () => {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const skillCategories = useMemo(() => [
    {
      title: t('skills.languagesFrameworks'),
      skills: [
        { name: 'PHP', icon: SiPhp, color: '#777BB4' },
        { name: 'Laravel', icon: SiLaravel, color: '#FF2D20' },
        { name: 'JavaScript', icon: SiJavascript, color: '#F7DF1E' },
        { name: 'TypeScript', icon: SiTypescript, color: '#3178C6' },
        { name: 'Python', icon: SiPython, color: '#3776AB' },
        { name: 'C#', icon: PiFileCSharpBold, color: '#239120' },
        { name: 'Java', icon: RiJavaLine, color: '#ED8B00' },
        { name: 'TensorFlow', icon: SiTensorflow, color: '#FF6F00' },
        { name: '.NET', icon: SiDotnet, color: '#512BD4' },
        { name: 'Node.js', icon: SiNodedotjs, color: '#339933' },
        { name: 'Express', icon: SiExpress, color: '#000000' },
        { name: 'React', icon: SiReact, color: '#61DAFB' },
        { name: 'Vue', icon: SiVuedotjs, color: '#4FC08D' },
        { name: 'Next.js', icon: SiNextdotjs, color: '#000000' },
        { name: 'Redux', icon: SiRedux, color: '#764ABC' },
      ],
    },
    {
      title: t('skills.databasesTools'),
      skills: [
        { name: 'MySQL', icon: SiMysql, color: '#4479A1' },
        { name: 'MongoDB', icon: SiMongodb, color: '#47A248' },
        { name: 'Redis', icon: SiRedis, color: '#DC382D' },
        { name: 'Tailwind', icon: SiTailwindcss, color: '#06B6D4' },
        { name: 'Bootstrap', icon: SiBootstrap, color: '#7952B3' },
      ],
    },
    {
      title: t('skills.devopsCloud'),
      skills: [
        { name: 'Docker', icon: SiDocker, color: '#2496ED' },
        { name: 'Azure', icon: FaCloud, color: '#0078D4' },
        { name: 'Git', icon: SiGit, color: '#F05032' },
        { name: 'Jest', icon: SiJest, color: '#C21325' },
      ],
    },
  ], [t, i18n.language]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const skillVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4 },
    },
  };

  return (
    <section id="skills" className="py-20 relative">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent"
          >
            {t('skills.title')}
          </motion.h2>

          <div className="space-y-12">
            {skillCategories.map((category, categoryIndex) => (
              <motion.div key={category.title} variants={itemVariants}>
                <h3 className="text-2xl font-semibold text-blue-400 mb-6 text-center md:text-left">
                  {category.title}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {category.skills.map((skill, index) => {
                    const Icon = skill.icon;
                    return (
                      <motion.div
                        key={skill.name}
                        variants={skillVariants}
                        whileHover={{ scale: 1.1, y: -5 }}
                        className="glass p-6 rounded-xl flex flex-col items-center justify-center space-y-3 glow-hover cursor-pointer"
                      >
                        <Icon className="text-4xl" style={{ color: skill.color }} />
                        <span className="text-sm text-gray-300 text-center">{skill.name}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional expertise */}
          <motion.div variants={itemVariants} className="mt-16">
            <h3 className="text-2xl font-semibold text-blue-400 mb-6 text-center">
              {t('skills.coreCompetencies')}
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                t('skills.competencies.microservices'),
                t('skills.competencies.restful'),
                t('skills.competencies.realtime'),
                t('skills.competencies.distributed'),
                t('skills.competencies.cloud'),
                t('skills.competencies.unitTesting'),
                t('skills.competencies.cicd'),
                t('skills.competencies.agile'),
              ].map((competency, index) => (
                <motion.span
                  key={index}
                  variants={skillVariants}
                  whileHover={{ scale: 1.05 }}
                  className="glass px-6 py-3 rounded-full text-gray-300 glow-hover cursor-default"
                >
                  {competency}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
